import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { API, setAuthToken, getAuthToken } from "@/lib/api";

/**
 * Landing spot after the backend finished the Google OAuth handshake.
 *
 * The backend has already created the session and set the httpOnly cookie;
 * it appends the same token to the URL *fragment* (`#session_token=...`).
 * Fragments never reach a server, so this does not leak into access logs
 * or Referer headers — but it does let us keep the token as a Bearer
 * fallback for browsers that silently drop the cookie (Safari ITP, strict
 * tracking prevention, mobile webviews).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { acceptLogin } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    // Idempotent — guards against React StrictMode double-invoke.
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionToken = params.get("session_token");

    // Drop the token out of the address bar so it does not linger in
    // history or get copy-pasted around.
    if (sessionToken) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    (async () => {
      try {
        if (sessionToken) setAuthToken(sessionToken);

        // The cookie alone may be enough, but we verify explicitly so a
        // dropped cookie surfaces as a clear error instead of a login loop.
        const t = getAuthToken();
        const res = await fetch(`${API}/auth/me`, {
          credentials: "include",
          headers: t ? { Authorization: `Bearer ${t}` } : undefined,
        });
        if (!res.ok) {
          console.error("[Auth] /auth/me rejected both cookie and bearer", res.status);
          setAuthToken(null);
          navigate("/?auth_error=session_rejected", { replace: true });
          return;
        }
        const u = await res.json();
        console.log("[Auth] signed in as", u.email, "is_admin:", u.is_admin);

        acceptLogin(u);
        navigate(u.is_admin ? "/admin" : "/", { replace: true, state: { user: u } });
      } catch (e) {
        console.error("Auth callback failed", e);
        const msg = encodeURIComponent(String(e?.message || e).slice(0, 200));
        navigate(`/?auth_error=verify_failed&msg=${msg}`, { replace: true });
      }
    })();
  }, [navigate, acceptLogin]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground">
      <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
      <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
        Signing you in…
      </div>
    </div>
  );
}
